import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { imobiliariaDataset } from '../../shared/data/imobiliariaData';
import { Home, Search, Heart, User, MapPin, TrendingUp, Bell, ChevronRight, FileText, CheckCircle2 } from 'lucide-react';

export const RealEstateApp: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  const [activeTab, setActiveTab] = useState<'inicio' | 'portfolio' | 'favoritos' | 'perfil'>('inicio');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const rentedProperties = imobiliariaDataset.filter(p => p.purpose === 'Aluguel' || p.purpose === 'Ambos').slice(0, 2);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans">
      <div className="w-[390px] h-[844px] bg-[var(--ym-prospect-surface)] text-[var(--ym-prospect-fg)] rounded-[3rem] shadow-2xl relative overflow-hidden border-[12px] border-slate-800">
        
        {/* StatusBar Mock */}
        <div className="h-12 flex items-center justify-between px-8 text-xs font-medium pt-2">
           <span>9:41</span>
           <div className="flex gap-1.5 items-center">
              <div className="w-4 h-3 bg-current rounded-sm"></div>
              <div className="w-3 h-3 bg-current rounded-full"></div>
           </div>
        </div>

        {/* Header App */}
        <div className="px-6 pt-4 pb-2">
           <div className="flex justify-between items-center mb-6">
              <div>
                 <p className="text-[10px] text-[var(--ym-prospect-muted)] uppercase tracking-widest font-bold mb-1">Portal do Proprietário</p>
                 <h1 className="font-heading font-black text-2xl text-[var(--ym-prospect-primary)]">Olá, Augusto.</h1>
              </div>
              <div className="w-10 h-10 rounded-full border border-[var(--ym-prospect-border)] bg-[var(--ym-prospect-bg)] flex items-center justify-center text-[var(--ym-prospect-primary)]">
                 <Bell className="w-5 h-5" />
              </div>
           </div>
        </div>

        {/* Content Area */}
        <div className="px-6 h-full overflow-y-auto pb-32 ym-hide-scrollbar">
           
           {/* Card Rentabilidade */}
           <div className="bg-[var(--ym-prospect-primary)] text-[var(--ym-prospect-bg)] p-6 rounded-2xl mb-8 relative overflow-hidden shadow-xl shadow-[var(--ym-prospect-primary)]/20">
              <div className="absolute top-0 right-0 p-4 opacity-20"><TrendingUp className="w-24 h-24" /></div>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-80">Rendimentos do Mês</p>
              <h2 className="text-3xl font-black font-heading mb-4">R$ 23.500,00</h2>
              <div className="flex items-center gap-2 text-xs font-medium">
                 <span className="bg-[var(--ym-prospect-bg)]/20 px-2 py-1 rounded">2 Imóveis Locados</span>
                 <span>Recebimento em 05/08</span>
              </div>
           </div>

           <h3 className="font-heading font-bold text-lg mb-4 text-[var(--ym-prospect-primary)]">Seu Portfólio</h3>
           
           <div className="space-y-4 mb-8">
              {rentedProperties.map(prop => (
                 <div key={prop.id} className="bg-[var(--ym-prospect-bg)] border border-[var(--ym-prospect-border)] rounded-2xl p-3 flex gap-4 items-center">
                    <img src={prop.images[0]} alt={prop.title} className="w-20 h-20 rounded-xl object-cover" />
                    <div className="flex-1">
                       <h4 className="text-sm font-bold text-[var(--ym-prospect-primary)] line-clamp-1">{prop.title}</h4>
                       <p className="text-[10px] text-[var(--ym-prospect-muted)] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/> {prop.location.neighborhood}</p>
                       <p className="text-xs font-bold mt-2">R$ {prop.priceAluguel?.toLocaleString('pt-BR')}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[var(--ym-prospect-muted)]" />
                 </div>
              ))}
           </div>

           <h3 className="font-heading font-bold text-lg mb-4 text-[var(--ym-prospect-primary)]">Documentos Recentes</h3>
           <div className="bg-[var(--ym-prospect-bg)] border border-[var(--ym-prospect-border)] rounded-2xl p-4 flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                 <div className="w-10 h-10 rounded-full bg-[var(--ym-prospect-surface)] flex items-center justify-center text-[var(--ym-prospect-primary)]">
                    <FileText className="w-5 h-5" />
                 </div>
                 <div>
                    <h4 className="text-xs font-bold text-[var(--ym-prospect-primary)]">Repasse_Agosto.pdf</h4>
                    <p className="text-[10px] text-[var(--ym-prospect-muted)]">Gerado há 2 horas</p>
                 </div>
              </div>
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
           </div>

        </div>

        {/* Bottom Nav */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-[var(--ym-prospect-surface)]/90 backdrop-blur-md border-t border-[var(--ym-prospect-border)] flex justify-around items-center px-6 pb-6">
           <button onClick={() => setActiveTab('inicio')} className={`flex flex-col items-center gap-1 ${activeTab === 'inicio' ? 'text-[var(--ym-prospect-primary)]' : 'text-[var(--ym-prospect-muted)]'}`}>
              <Home className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Início</span>
           </button>
           <button onClick={() => setActiveTab('portfolio')} className={`flex flex-col items-center gap-1 ${activeTab === 'portfolio' ? 'text-[var(--ym-prospect-primary)]' : 'text-[var(--ym-prospect-muted)]'}`}>
              <Search className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Explorar</span>
           </button>
           <button onClick={() => setActiveTab('favoritos')} className={`flex flex-col items-center gap-1 ${activeTab === 'favoritos' ? 'text-[var(--ym-prospect-primary)]' : 'text-[var(--ym-prospect-muted)]'}`}>
              <Heart className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Favoritos</span>
           </button>
           <button onClick={() => setActiveTab('perfil')} className={`flex flex-col items-center gap-1 ${activeTab === 'perfil' ? 'text-[var(--ym-prospect-primary)]' : 'text-[var(--ym-prospect-muted)]'}`}>
              <User className="w-6 h-6" />
              <span className="text-[9px] font-bold uppercase tracking-wider">Perfil</span>
           </button>
        </div>

      </div>
      
      <div className="absolute top-10 right-10 text-slate-500 text-sm max-w-xs hidden xl:block">
        <p className="font-bold mb-2 text-slate-800">Conceito de App</p>
        <p>Wireframe demonstrativo do módulo mobile (App Proprietário) para {prospect.trade_name}.</p>
      </div>
    </div>
  );
};
